import { Injectable, signal } from '@angular/core';
import { environment } from '@env/environment';

/**
 * How the rest of the app opens and closes the feedback panel: the header's Feedback button and the Help pages' feedback
 * card. The panel itself lives in ic-feedback-widget, which reports back whether it is open.
 *
 * A request is held until the widget takes it, because the widget loads in its own chunk once the page is idle and a
 * click can come first.
 */
@Injectable({ providedIn: 'root' })
export class FeedbackPanelService {
    /** Off unless the environment has a flow URL, as the widget is; the entry points hide with it. */
    readonly enabled = !!environment.feedbackFlowUrl;

    private readonly _pending = signal(false);
    readonly pending = this._pending.asReadonly();

    private readonly _closeRequested = signal(false);
    readonly closeRequested = this._closeRequested.asReadonly();

    private readonly _isOpen = signal(false);
    /** Whether the panel is showing, as the widget last reported it; drives the header button's pressed state. */
    readonly isOpen = this._isOpen.asReadonly();

    open(): void {
        if (this.enabled) {
            this._closeRequested.set(false);
            this._pending.set(true);
        }
    }

    /** Closes it, keeping the draft, as Cancel does; nothing happens when it is already shut. */
    close(): void {
        this._pending.set(false);

        if (this._isOpen()) {
            this._closeRequested.set(true);
        }
    }

    toggle(): void {
        if (this._isOpen()) {
            this.close();
        } else {
            this.open();
        }
    }

    /** The widget takes the request; true when there was one to take. */
    take(): boolean {
        const pending = this._pending();
        this._pending.set(false);
        return pending;
    }

    /** The widget takes a close request; true when there was one to take. */
    takeClose(): boolean {
        const requested = this._closeRequested();
        this._closeRequested.set(false);
        return requested;
    }

    /** Called by the widget whenever the panel opens or closes. */
    setOpen(open: boolean): void {
        this._isOpen.set(open);
    }
}
